/**
 * Kiosk inactivity: return to dashboard after 5m; sleep overlay after 10m
 * on controller / regular (not display). Sleep uses the lobby attract screen.
 * Also hosts Advanced → Preview sleep screen for desktop testing.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useKioskRole, isKioskRole } from '../hooks/useKioskRole'
import { useAuth } from '../contexts/AuthContext'
import { useSettings } from '../contexts/SettingsContext'
import { patchKioskPayload } from '../utils/kioskDisplaySync'
import KioskLobbyAttract from './KioskLobbyAttract'

const HOME_AFTER_MS = 5 * 60 * 1000
const SLEEP_AFTER_MS = 10 * 60 * 1000
const WAKE_MESSAGE = 'Touch the screen to wake'

function KioskSleepOverlay({
  onWake,
  dismissOnAnyInput = false,
}: {
  onWake: () => void
  /** Preview mode: any pointer or key dismisses (not only Enter/Space/Escape). */
  dismissOnAnyInput?: boolean
}) {
  useEffect(() => {
    if (!dismissOnAnyInput) return
    const wake = () => onWake()
    window.addEventListener('pointerdown', wake, true)
    window.addEventListener('keydown', wake, true)
    window.addEventListener('wheel', wake, true)
    return () => {
      window.removeEventListener('pointerdown', wake, true)
      window.removeEventListener('keydown', wake, true)
      window.removeEventListener('wheel', wake, true)
    }
  }, [dismissOnAnyInput, onWake])

  return (
    <div
      className="fixed inset-0 z-[10000] cursor-pointer"
      role="button"
      tabIndex={0}
      aria-label={WAKE_MESSAGE}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onWake()
      }}
      onKeyDown={(e) => {
        if (dismissOnAnyInput || e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
          e.preventDefault()
          onWake()
        }
      }}
    >
      <KioskLobbyAttract className="pointer-events-none" />
    </div>
  )
}

export default function KioskInactivityGuard() {
  const role = useKioskRole()
  const { user } = useAuth()
  const { sleepPreviewOpen, closeSleepPreview } = useSettings()
  const navigate = useNavigate()
  const location = useLocation()
  const [sleeping, setSleeping] = useState(false)

  const lastActivityRef = useRef(Date.now())
  const homeFiredForIdleRef = useRef(false)
  const sleepingRef = useRef(false)
  sleepingRef.current = sleeping

  const active = Boolean(user?.isKiosk && isKioskRole(role))
  /** Controller / regular own idle actions; display follows via route sync. */
  const ownsIdle = active && role !== 'display'

  const goHome = useCallback(() => {
    const onHome = location.pathname === '/' && !location.search
    if (!onHome) {
      navigate('/', { replace: true })
    }
    // Clear resources detail on the display when idling home.
    if (role === 'controller' || role === 'regular') {
      void patchKioskPayload(
        { resources: { selectedKey: null, selectionToken: Date.now() } },
        role,
      )
    }
  }, [location.pathname, location.search, navigate, role])

  const markActivity = useCallback(() => {
    lastActivityRef.current = Date.now()
    homeFiredForIdleRef.current = false
    if (sleepingRef.current) setSleeping(false)
  }, [])

  const wake = useCallback(() => {
    markActivity()
  }, [markActivity])

  // Activity listeners (controller / regular)
  useEffect(() => {
    if (!ownsIdle) return

    const onActivity = () => {
      // While sleeping, only the overlay wake handler should clear sleep.
      if (sleepingRef.current) return
      markActivity()
    }

    const events: Array<keyof WindowEventMap> = [
      'pointerdown',
      'keydown',
      'touchstart',
      'wheel',
    ]
    for (const ev of events) {
      window.addEventListener(ev, onActivity, { passive: true })
    }
    return () => {
      for (const ev of events) {
        window.removeEventListener(ev, onActivity)
      }
    }
  }, [ownsIdle, markActivity])

  // Idle timers
  useEffect(() => {
    if (!ownsIdle) return

    const id = window.setInterval(() => {
      const idle = Date.now() - lastActivityRef.current
      if (idle >= HOME_AFTER_MS && !homeFiredForIdleRef.current) {
        homeFiredForIdleRef.current = true
        goHome()
      }
      if (idle >= SLEEP_AFTER_MS && !sleepingRef.current) {
        setSleeping(true)
      }
    }, 1000)

    return () => window.clearInterval(id)
  }, [ownsIdle, goHome])

  if (sleepPreviewOpen) {
    return <KioskSleepOverlay onWake={closeSleepPreview} dismissOnAnyInput />
  }

  if (!ownsIdle || !sleeping) return null

  return <KioskSleepOverlay onWake={wake} />
}
